import { useState, useCallback, useEffect } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const steps = [
  { key: 'manifesto', labelKey: 'onboarding.step_manifesto' },
  { key: 'location', labelKey: 'onboarding.step_location' },
  { key: 'reader', labelKey: 'onboarding.step_reader' },
  { key: 'name', labelKey: 'onboarding.step_name' },
  { key: 'share', labelKey: 'onboarding.step_share' },
  { key: 'done', labelKey: 'onboarding.step_done' },
]

export default function OnboardingLayout() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const [visible, setVisible] = useState(false)

  // Derive current step from URL
  const currentPath = location.pathname.split('/').pop() || 'manifesto'
  const stepIndex = steps.findIndex((s) => s.key === currentPath)
  const currentStep = stepIndex >= 0 ? stepIndex : 0

  // Entrance animation
  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 50)
    return () => clearTimeout(t)
  }, [])

  const goToStep = useCallback(
    (index: number) => {
      if (index >= 0 && index < steps.length) {
        navigate(`/onboarding/${steps[index].key}`)
      }
    },
    [navigate]
  )

  const canGoBack = currentStep > 0
  const canGoForward = currentStep < steps.length - 1

  const handleBack = useCallback(() => {
    if (canGoBack) goToStep(currentStep - 1)
  }, [canGoBack, currentStep, goToStep])

  const handleForward = useCallback(() => {
    if (canGoForward) goToStep(currentStep + 1)
  }, [canGoForward, currentStep, goToStep])

  return (
    <div className="min-h-screen bg-parchment">
      {/* Progress indicator */}
      <div
        className={`pt-8 pb-4 px-6 transition-all duration-700 ${
          visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'
        }`}
      >
        <div className="max-w-md mx-auto">
          {/* Step dots */}
          <div className="flex items-center justify-between relative">
            {/* Connecting line */}
            <div className="absolute left-0 right-0 top-[10px] h-[2px] bg-silver/30 -z-10" />

            {steps.map((step, i) => {
              const isCompleted = i < currentStep
              const isCurrent = i === currentStep
              const isUpcoming = i > currentStep

              return (
                <div
                  key={step.key}
                  className="flex flex-col items-center gap-2 cursor-pointer"
                  onClick={() => i <= currentStep && goToStep(i)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && i <= currentStep) goToStep(i)
                  }}
                >
                  <div
                    className={`w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                      isCompleted
                        ? 'bg-green/50 border-green/50'
                        : isCurrent
                          ? 'bg-green border-green scale-110'
                          : 'bg-parchment border-silver/50'
                    }`}
                  />
                  <span
                    className={`text-[10px] font-body transition-colors duration-300 ${
                      isCurrent
                        ? 'text-green font-medium'
                        : isCompleted
                          ? 'text-ink-muted'
                          : 'text-ink-muted/40'
                    }`}
                  >
                    {t(step.labelKey)}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Content area */}
      <div
        className={`flex-1 px-6 pb-8 transition-all duration-500 delay-100 ${
          visible ? 'opacity-100' : 'opacity-0'
        }`}
      >
        <div className="max-w-lg mx-auto">
          <Outlet context={{ currentStep, goToStep, handleForward, handleBack }} />
        </div>
      </div>
    </div>
  )
}
